const PERSIST_PROGRESS = false;

/**
 * Sets a value on a React-controlled input in a way React recognises.
 * Calling `input.value = x` only writes to the DOM; React's own internal
 * state stays empty and overwrites the DOM on any re-render. Using the
 * native prototype setter bypasses React's override, and the synthetic
 * `input` event triggers React's event delegation so it updates its state.
 * `composed: true` lets the event cross the shadow DOM boundary.
 * @param {HTMLInputElement} input
 * @param {string} value
 */
function setReactInputValue(input, value) {
  const nativeSetter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    "value",
  )?.set;
  if (nativeSetter) {
    nativeSetter.call(input, value);
  } else {
    input.value = value;
  }
  input.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
}

/**
 * @typedef {Object} QuizState
 * @property {number}  sectionIndex
 * @property {number}  questionIndex
 * @property {number}  sectionCorrectCount
 * @property {number}  consecutiveCorrect
 * @property {number}  totalCorrect
 * @property {number}  totalAnswered
 * @property {boolean} skipOfferDeclined
 * @property {boolean} answered
 */

class SpanishLevelTest extends HTMLElement {
  /** @type {QuizState} */
  state = /** @type {QuizState} */ ({});

  /**
   * Runs when the element is inserted into the page. Parses the embedded JSON
   * quiz data, caches DOM references, wires up event listeners, and either
   * restores saved progress or renders the first question.
   */
  connectedCallback() {
    const dataEl = document.getElementById(
      `slt-data-${this.dataset.sectionId}`,
    );
    if (!dataEl)
      return console.error("Spanish Level Test metaobject data missing");

    try {
      this.quizData = JSON.parse(dataEl.textContent);
    } catch {
      return console.error(
        "Unable to process metaobject data for Spanish Level Test",
      );
    }

    if (!this.quizData.sections?.length)
      return console.error(
        "Unable to process metaobject data for Spanish Level Test",
      );

    this.state = {
      sectionIndex: 0,
      questionIndex: 0,
      sectionCorrectCount: 0,
      consecutiveCorrect: 0,
      totalCorrect: 0,
      totalAnswered: 0,
      skipOfferDeclined: false,
      answered: false,
    };

    this.els = {
      progress: this.querySelector(".slt__progress"),
      stage: this.querySelector(".slt__stage"),
      image: this.querySelector(".slt__image"),
      questionText: this.querySelector(".slt__question-text"),
      feedback: this.querySelector(".slt__feedback"),
      actionBtn: this.querySelector(".slt__action-btn"),
      skipOffer: this.querySelector(".slt__skip-offer"),
      skipMsg: this.querySelector(".slt__skip-msg"),
      skipYes: this.querySelector(".slt__skip-yes"),
      skipNo: this.querySelector(".slt__skip-no"),
      form: this.querySelector(".slt__form"),
    };

    // Delegate change events from answer inputs up to the component root
    // so the listener survives innerHTML replacements and show/hide cycles.
    this.addEventListener("change", (e) => {
      if (/** @type {Element} */ (e.target).closest(".slt__answers")) {
        this.onAnswerChange();
      }
    });
    this.els.actionBtn.addEventListener("click", () => this.onActionClick());
    this.els.skipYes.addEventListener("click", () => this.onSkipYes());
    this.els.skipNo.addEventListener("click", () => this.onSkipNo());

    const saved = this.loadProgress();
    if (saved) {
      Object.assign(this.state, saved.state, { answered: false });
      if (saved.finished) {
        this.renderProgress();
        this.finish(saved.finished.levelText, saved.finished.levelUrl);
      } else {
        this.renderQuestion(); // renderQuestion calls renderProgress internally
      }
    } else {
      this.renderQuestion();
    }
  }

  // ── Accessors ────────────────────────────────────────────────

  /** The currently active section object from quizData. */
  get section() {
    return this.quizData.sections[this.state.sectionIndex];
  }

  /** The currently active question within the current section. */
  get question() {
    return this.section.questions[this.state.questionIndex];
  }

  /** Whether the user is answering questions in the final section. */
  get isLastSection() {
    return this.state.sectionIndex === this.quizData.sections.length - 1;
  }

  /** localStorage key scoped to this section instance. */
  get storageKey() {
    return `slt-progress-${this.dataset.sectionId}`;
  }

  /**
   * Structural fingerprint of the quiz content. Used to discard stale saves
   * when questions are added, removed, or reordered in the CMS.
   */
  get dataChecksum() {
    const counts = this.quizData.sections
      .map((s) => s.questions.length)
      .join(",");
    return `${this.quizData.sections.length}:${counts}`;
  }

  // ── Progress persistence ──────────────────────────────────────

  /**
   * Serialises the current quiz state to localStorage so the user can resume
   * after accidental navigation. Pass `{ levelText, levelUrl }` as `finished`
   * once the quiz ends; pass nothing (null) during the quiz itself.
   * @param {{ levelText: string, levelUrl: string } | null} finished
   */
  saveProgress(finished = null) {
    if (!PERSIST_PROGRESS) return;
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          checksum: this.dataChecksum,
          state: { ...this.state, answered: false },
          finished, // null during quiz, { levelText, levelUrl } when done
        }),
      );
    } catch {
      // localStorage unavailable (private mode, storage quota)
    }
  }

  /**
   * Reads saved state from localStorage and validates it against the current
   * quiz structure. Returns null if nothing is saved or if the content has
   * changed since the save was written.
   */
  loadProgress() {
    if (!PERSIST_PROGRESS) return null;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return null;
      const saved = JSON.parse(raw);
      if (saved.checksum !== this.dataChecksum) {
        localStorage.removeItem(this.storageKey);
        return null;
      }
      return saved;
    } catch {
      return null;
    }
  }

  /** Removes the saved quiz state from localStorage. Called on form submit. */
  clearProgress() {
    if (!PERSIST_PROGRESS) return;
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // ignore
    }
  }

  /**
   * Live DOM query for the answers container. Re-queried on every access to
   * guard against stale references after the stage is hidden and re-shown by
   * Shopify theme transitions.
   */
  get answersEl() {
    return this.querySelector(".slt__answers");
  }

  // ── Rendering ────────────────────────────────────────────────

  /**
   * Updates the section progress pip strip, marking earlier sections as done
   * and the current section as active.
   */
  renderProgress() {
    const steps = this.els.progress.querySelectorAll(".slt__progress-step");
    steps.forEach((/** @type {Element} */ step, i) => {
      step.classList.toggle("is-done", i < this.state.sectionIndex);
      step.classList.toggle("is-current", i === this.state.sectionIndex);
    });
  }

  /**
   * Renders the current question: updates the image, question text, and answer
   * options (radio for single-correct, checkboxes for multi-correct), then
   * resets the action button to its initial "Submit" state.
   */
  renderQuestion() {
    const q = this.question;
    this.state.answered = false;
    this.saveProgress();

    // Image
    if (q.image) {
      this.els.image.src = q.image;
      this.els.image.alt = q.imageAlt ?? "";
      this.els.image.removeAttribute("hidden");
    } else {
      this.els.image.hidden = true;
    }

    // Question text
    this.els.questionText.textContent = q.question;

    const correctAnswers = /** @type {string[]} */ (
      Array.isArray(q.correctAnswers) ? q.correctAnswers : []
    );
    const name = `slt-q-${this.state.sectionIndex}-${this.state.questionIndex}`;

    // Re-query the live element every render to survive theme show/hide transitions
    const answersEl = this.answersEl;
    answersEl.innerHTML = ["A", "B", "C", "D"]
      .filter((letter) => q.answers?.[letter])
      .map((letter) => {
        const id = `${name}-${letter}`;
        return `<label class="slt__answer" for="${id}">
          <span class="slt__answer-letter">${letter}:</span>
          <span class="slt__answer-text">${q.answers[letter]}</span>
          <input type="radio" id="${id}" name="${name}" value="${letter}" />
          <span class="slt__answer-indicator" aria-hidden="true"></span>
        </label>`;
      })
      .join("");

    // Reset UI
    this.els.feedback.textContent = "";
    this.els.feedback.className = "slt__feedback";
    this.els.actionBtn.textContent = "Submit";
    this.els.actionBtn.dataset.phase = "submit";
    this.els.actionBtn.hidden = true;

    this.renderProgress();
  }

  // ── Interaction handlers ─────────────────────────────────────

  /**
   * Fires whenever an answer input changes. Syncs the `is-selected` class on
   * each answer label then shows or hides the Submit button depending on whether anything is checked.
   */
  onAnswerChange() {
    if (this.state.answered) return;
    const answersEl = this.answersEl;
    if (!answersEl) return;

    answersEl
      .querySelectorAll(".slt__answer")
      .forEach((/** @type {Element} */ wrap) => {
        const input = /** @type {HTMLInputElement} */ (
          wrap.querySelector("input")
        );
        wrap.classList.toggle("is-selected", input?.checked ?? false);
      });

    this.els.actionBtn.hidden = !answersEl.querySelector("input:checked");
  }

  /**
   * Routes action button clicks to the correct handler based on the button's
   * current `data-phase` value: submit, terminate, offer-skip, or continue.
   */
  onActionClick() {
    const phase = this.els.actionBtn.dataset.phase;
    if (phase === "submit") {
      this.submitAnswer();
    } else if (phase === "terminate") {
      this.terminate();
    } else if (phase === "offer-skip") {
      this.showSkipOffer();
    } else {
      this.nextQuestion();
    }
  }

  /**
   * Evaluates the selected answer(s), updates running totals and consecutive
   * streaks, reveals correct/wrong state on all answer options with appropriate
   * classes, displays feedback text, and updates the action button to the next
   * appropriate phase (terminate, offer-skip, or continue).
   */
  submitAnswer() {
    if (this.state.answered) return;
    this.state.answered = true;

    const q = this.question;
    const answersEl = this.answersEl;
    const selected = Array.from(
      answersEl.querySelectorAll("input:checked"),
    ).map((/** @type {HTMLInputElement} */ i) => i.value.toUpperCase());
    // Normalise to uppercase so stored values like "a" match input values "A"
    const correct = (
      Array.isArray(q.correctAnswers) ? q.correctAnswers : []
    ).map((a) => String(a).toUpperCase());

    // Single-select (radio), so correct = the selected answer is among the correct answers
    const isCorrect =
      selected.length > 0 && selected.every((v) => correct.includes(v));

    // Update running counts
    this.state.totalAnswered++;
    if (isCorrect) {
      this.state.sectionCorrectCount++;
      this.state.totalCorrect++;
      this.state.consecutiveCorrect++;
    } else {
      this.state.consecutiveCorrect = 0;
    }

    // Reveal correct/wrong state on all options
    answersEl
      .querySelectorAll(".slt__answer")
      .forEach((/** @type {Element} */ wrap) => {
        const input = /** @type {HTMLInputElement} */ (
          wrap.querySelector("input")
        );
        const wasChecked = input.checked;
        input.disabled = true;
        if (correct.includes(input.value)) {
          wrap.classList.add("is-correct");
        } else {
          wrap.classList.add("is-wrong");
        }
        if (!wasChecked) wrap.classList.add("is-unselected");
      });

    // Feedback text
    if (isCorrect) {
      this.els.feedback.textContent = "Correct!";
      this.els.feedback.className = "slt__feedback is-correct";
    } else {
      const correctLabels = correct.map((c) => q.answers[c]).join(", ");
      this.els.feedback.textContent = `Incorrect. The correct answer${correct.length > 1 ? "s are" : " is"}: ${correctLabels}`;
      this.els.feedback.className = "slt__feedback is-wrong";
    }

    // Determine next action
    const sectionQCount = this.section.questions.length;
    const sectionAnswered = this.state.questionIndex + 1;
    const minBeforeCheck = Math.max(3, Math.ceil(sectionQCount / 3));
    const accuracyThreshold = 1 - this.section.percentageWrongToLockLevel / 100;
    const accuracy = this.state.sectionCorrectCount / sectionAnswered;

    if (sectionAnswered >= minBeforeCheck && accuracy < accuracyThreshold) {
      this.els.actionBtn.textContent = "See your results";
      this.els.actionBtn.dataset.phase = "terminate";
    } else {
      const skipThreshold = Math.max(
        3,
        Math.ceil((sectionQCount * this.section.skipAheadPercentage) / 100),
      );
      const isLastQuestion = sectionAnswered >= sectionQCount;
      const offerSkip =
        !isLastQuestion &&
        !this.isLastSection &&
        this.state.consecutiveCorrect >= skipThreshold &&
        !this.state.skipOfferDeclined;

      this.els.actionBtn.textContent = "Continue";
      this.els.actionBtn.dataset.phase = offerSkip ? "offer-skip" : "continue";
    }

    this.els.actionBtn.hidden = false;
  }

  // ── Quiz flow ────────────────────────────────────────────────

  /**
   * Advances to the next question in the current section. If all questions in
   * the section have been answered, evaluates the section result instead.
   */
  nextQuestion() {
    const next = this.state.questionIndex + 1;
    if (next >= this.section.questions.length) {
      this.evaluateSection(false);
    } else {
      this.state.questionIndex = next;
      this.renderQuestion();
    }
  }

  /**
   * Scores the completed section and decides what happens next.
   *
   * A full pass (triggered by the skip-ahead offer) always moves to the next
   * section or finishes at the highest level. Otherwise the correct-answer
   * ratio is compared against three sub-level thresholds (each covering a
   * quarter of the question count). If the ratio is high enough the user
   * passes and moves on; otherwise `finish` is called with the matching
   * sub-level collection.
   *
   * @param {boolean} isFullPass - true when the user chose to skip ahead
   */
  evaluateSection(isFullPass) {
    if (isFullPass) {
      if (this.isLastSection) {
        this.finish(
          this.quizData.advancedLevel.title,
          this.quizData.advancedLevel.url,
        );
      } else {
        this.advanceToNextSection();
      }
      return;
    }

    const sectionQCount = this.section.questions.length;
    // categorySplit = questionCount / (subLevels + 1) = questionCount / 4
    const categorySplit = sectionQCount / 4;
    const correct = this.state.sectionCorrectCount;

    let subLevelIndex = -1; // -1 = section passed
    for (let pos = 1; pos <= 3; pos++) {
      if (correct < categorySplit * pos) {
        subLevelIndex = pos - 1;
        break;
      }
    }

    if (subLevelIndex === -1) {
      // Passed — move to next section or finish as highest level
      if (this.isLastSection) {
        this.finish(
          this.quizData.advancedLevel.title,
          this.quizData.advancedLevel.url,
        );
      } else {
        this.advanceToNextSection();
      }
    } else {
      const col = this.section.subLevelCollections[subLevelIndex];
      if (col) {
        this.finish(col.title, col.url);
      } else {
        this.finish(
          this.quizData.advancedLevel.title,
          this.quizData.advancedLevel.url,
        );
      }
    }
  }
  /**
   * @param {string} collectionTitle  - Title of the recommended course collection
   * @returns {string}
   */
  getLevelFromCollectionTitle(collectionTitle) {
    return (
      collectionTitle.split("Spanish Courses")[0]?.trim() || collectionTitle
    );
  }

  /**
   * Ends the quiz early when rolling accuracy falls below the section
   * threshold. Counts all remaining unanswered questions in the section as
   * wrong, then resolves the sub-level from the correct-answer ratio so far.
   * Terminating within the last section uses that section's sub-level
   * collections, not the final-level collection.
   */
  terminate() {
    // Determine sub-level from correct answers so far
    const sectionQCount = this.section.questions.length;
    const categorySplit = sectionQCount / 4;
    const correct = this.state.sectionCorrectCount;

    let subLevelIndex = 0;
    for (let pos = 1; pos <= 3; pos++) {
      if (correct < categorySplit * pos) {
        subLevelIndex = pos - 1;
        break;
      }
    }

    const col = this.section.subLevelCollections[subLevelIndex];
    if (col) {
      this.finish(col.title, col.url);
    } else {
      this.finish(
        this.quizData.advancedLevel.title,
        this.quizData.advancedLevel.url,
      );
    }
  }

  /**
   * Resets all per-section counters and renders the first question of the
   * next section.
   */
  advanceToNextSection() {
    this.state.sectionIndex++;
    this.state.questionIndex = 0;
    this.state.sectionCorrectCount = 0;
    this.state.consecutiveCorrect = 0;
    this.state.skipOfferDeclined = false;
    this.renderQuestion();
  }

  // ── Skip-ahead offer ─────────────────────────────────────────

  /**
   * Hides the question stage and shows the skip-ahead prompt after the user
   * builds a long consecutive-correct streak. The prompt names the destination
   * (next section or results) so the user can make an informed choice.
   */
  showSkipOffer() {
    const nextSection = this.quizData.sections[this.state.sectionIndex + 1];
    const destination = this.isLastSection
      ? "get your results"
      : `the ${nextSection?.name ?? "next"} section`;

    this.els.skipMsg.textContent = `Great work — you're on a strong streak! Would you like to skip ahead to ${destination}, or keep going in this section?`;

    this.els.stage.hidden = true;
    this.els.skipOffer.hidden = false;
  }

  /**
   * User accepted the skip offer. Dismisses the prompt and evaluates the
   * current section as a full pass, moving to the next section or finishing.
   */
  onSkipYes() {
    this.els.skipOffer.hidden = true;
    this.els.stage.hidden = false;
    this.evaluateSection(true);
  }

  /**
   * User declined the skip offer. Dismisses the prompt, marks the skip as
   * declined so it is not re-offered on the same streak, and continues to the
   * next question.
   */
  onSkipNo() {
    this.els.skipOffer.hidden = true;
    this.els.stage.hidden = false;
    // Mark skip as declined so we don't re-offer unless the streak resets
    this.state.skipOfferDeclined = true;
    this.nextQuestion();
  }

  // ── Finish ───────────────────────────────────────────────────

  /**
   * Ends the quiz: hides the question stage, skip offer, and progress bar,
   * reveals the results form, then populates the Shopify Forms fields with the
   * user's determined level.
   * @param {string} levelText - Human-readable level name (e.g. "Pre-Intermediate A2.2")
   * @param {string} levelUrl  - URL of the recommended course collection
   */
  finish(levelText, levelUrl) {
    this.saveProgress({ levelText, levelUrl });

    this.els.stage.hidden = true;
    this.els.skipOffer.hidden = true;
    this.els.progress.hidden = true;
    this.els.form.hidden = false;

    //Converts 'Advanced Courses' to 'Advanced' etc
    levelText = this.getLevelFromCollectionTitle(levelText);

    try {
      sessionStorage.setItem(
        "slt-result",
        JSON.stringify({
          levelText,
          levelUrl,
          totalCorrect: this.state.totalCorrect,
          totalAnswered: this.state.totalAnswered,
        }),
      );
    } catch {}

    this.populateFormFields(levelText, levelUrl);
  }

  /**
   * Populates the quiz-result hidden fields inside the Shopify Forms shadow
   * root, then hides those field containers so the user only sees the name,
   * email, and other manually-filled fields. Retries on a short delay because
   * the Shopify Forms block may not have finished rendering when this runs.
   * Also re-populates values just before form submission in case the React
   * form library resets them during validation.
   * @param {string} levelText - Human-readable level name
   * @param {string} levelUrl  - URL of the recommended course collection
   */
  populateFormFields(levelText, levelUrl) {
    const { totalCorrect, totalAnswered } = this.state;

    // Map exact field IDs (from Shopify Forms custom fields) to their values
    const fieldValues = {
      "custom#slt_assigned_spanish_level": levelText,
      "custom#slt_correct_answer_count": String(totalCorrect),
      "custom#slt_recommended_courses_url": levelUrl,
      "custom#slt_questions_answered_count": String(totalAnswered),
    };

    const fill = () => {
      const form = this.els?.form;
      if (!form) return;
      // Shopify Forms renders inside an open shadow root — pierce it
      /** @type {ShadowRoot | Element} */
      let root = form;
      for (const el of form.querySelectorAll("*")) {
        if (el.shadowRoot) {
          root = el.shadowRoot;
          break;
        }
      }

      Object.entries(fieldValues).forEach(([id, value]) => {
        const input = /** @type {HTMLInputElement | null} */ (
          root.querySelector(`[id="${id}"]`)
        );
        if (!input) return;
        // Use the React-aware setter so the value survives re-renders
        setReactInputValue(input, value);
        // Hide the field wrapper so quiz-data fields aren't visible to the user
        const wrap = /** @type {HTMLElement | null} */ (
          input.closest('[class*="formFieldContainer"]')
        );
        if (wrap) wrap.style.display = "none";
      });

      // Re-apply values on submit-button click (capture phase runs before
      // React's own click handler, so values are correct before validation)
      const submitBtn = /** @type {HTMLElement | null} */ (
        root.querySelector('[type="submit"]')
      );
      if (submitBtn && !submitBtn.dataset.sltBound) {
        submitBtn.dataset.sltBound = "1";
        submitBtn.addEventListener(
          "click",
          () => {
            Object.entries(fieldValues).forEach(([id, value]) => {
              const input = /** @type {HTMLInputElement | null} */ (
                root.querySelector(`[id="${id}"]`)
              );
              if (input) setReactInputValue(input, value);
            });
            this.clearProgress();
          },
          { capture: true },
        );
      }
    };

    // Run immediately, then retry — shadow root may not be populated yet
    fill();
    setTimeout(fill, 300);
    setTimeout(fill, 1200);
  }
}

if (!customElements.get("spanish-level-test")) {
  customElements.define("spanish-level-test", SpanishLevelTest);
}
